import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Partners() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => setItems((await api.get("/partners")).data);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete partner?")) return;
    await api.delete(`/partners/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <AppLayout title="External Partners">
      <div className="flex justify-end mb-3">
        <NewDialog open={open} setOpen={setOpen} onCreated={load}/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && <div className="col-span-full text-center py-16 text-[var(--miro-text-muted)]">No external partners yet.</div>}
        {items.map(p => (
          <div key={p.id} data-testid={`partner-card-${p.id}`} className="miro-card p-5">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="miro-overline">{p.organisation || "PARTNER"}</div>
                <div className="font-display font-bold text-lg text-[var(--miro-navy)] mt-1">{p.name}</div>
                <div className="text-xs text-[var(--miro-text-muted)] mt-0.5 truncate">{p.contact_email}</div>
              </div>
              <button data-testid={`partner-delete-${p.id}`} onClick={() => remove(p.id)} className="text-[var(--miro-text-muted)] hover:text-red-600">
                <Trash2 size={14}/>
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Badge className="border-0 font-semibold" style={{ background: "#DCFCE7", color: "#15803D" }}>
                {p.commission_rate}% commission
              </Badge>
            </div>
            {p.tags?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {p.tags.map(t => (
                  <span key={t} className="text-[10px] px-2 py-0.5 rounded bg-[var(--miro-bg)] border border-[var(--miro-border)] text-[var(--miro-text-secondary)]">#{t}</span>
                ))}
              </div>
            )}
            {p.notes && <p className="text-xs text-[var(--miro-text-secondary)] mt-3 line-clamp-2">{p.notes}</p>}
          </div>
        ))}
      </div>
    </AppLayout>
  );
}

function NewDialog({ open, setOpen, onCreated }) {
  const [f, setF] = useState({ name: "", organisation: "", contact_email: "", commission_rate: 5, tags: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/partners", {
        ...f,
        commission_rate: Number(f.commission_rate) || 0,
        tags: f.tags.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Partner added");
      setOpen(false);
      setF({ name: "", organisation: "", contact_email: "", commission_rate: 5, tags: "", notes: "" });
      onCreated?.();
    } catch (e) { toast.error("Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-partner-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New partner</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New external partner</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name *</Label><Input data-testid="new-partner-name" value={f.name} onChange={e=>upd("name",e.target.value)} className="mt-1"/></div>
          <div><Label>Organisation</Label><Input data-testid="new-partner-org" value={f.organisation} onChange={e=>upd("organisation",e.target.value)} className="mt-1"/></div>
          <div><Label>Commission %</Label><Input data-testid="new-partner-comm" type="number" value={f.commission_rate} onChange={e=>upd("commission_rate",e.target.value)} className="mt-1"/></div>
          <div className="col-span-2"><Label>Email *</Label><Input data-testid="new-partner-email" type="email" value={f.contact_email} onChange={e=>upd("contact_email",e.target.value)} className="mt-1"/></div>
          <div className="col-span-2"><Label>Tags (comma)</Label><Input data-testid="new-partner-tags" value={f.tags} onChange={e=>upd("tags",e.target.value)} className="mt-1"/></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea data-testid="new-partner-notes" rows={2} value={f.notes} onChange={e=>upd("notes",e.target.value)} className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid="new-partner-submit" disabled={!f.name.trim()||!f.contact_email.trim()||saving} onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Saving…" : "Add partner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
